// src/components/MaterialModal.js
import React, { useState, useMemo } from "react";
import "./MaterialModal.css";
import { API_URL } from "../config";

/**
 * UWAGA:
 * Kategoria jest NA STAŁE ustawiona na "Inne".
 * (nie wyświetlamy już tego w UI, ale funkcjonalność zostaje)
 */
const FIXED_CATEGORY = "Inne";

function round2(n) {
  const x = Number(n);
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

function MaterialModal({ orderId, onClose, onSave }) {
  // ✅ Sekcja "Numery / identyfikatory" ukryta w UI (zostaje w kodzie i payload)
  const SHOW_IDENTIFIERS_SECTION = false;

  const [formData, setFormData] = useState({
    name: "",
    symbol: "",
    manufacturer: "",
    catalog_number: "",
    serial_number: "",
    mac_address: "",
    designation: "",
    quantity_type: "sztuki", // 'sztuki' | 'metry'
    quantity: "",
    price_per_piece: "",
    currency: "zł",
    additional_description: "",
  });

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const unitPriceLabel =
    formData.quantity_type === "metry" ? "Cena za metr: *" : "Cena za sztukę: *";

  const totalPrice = useMemo(() => {
    const qty = Number(formData.quantity);
    const ppp = Number(formData.price_per_piece);
    if (Number.isNaN(qty) || Number.isNaN(ppp) || qty <= 0 || ppp < 0) return 0;
    return round2(qty * ppp);
  }, [formData.quantity, formData.price_per_piece]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const name = (formData.name || "").trim();
    const qty = Number(formData.quantity);
    const ppp = Number(formData.price_per_piece);

    if (!name) {
      setError("Nazwa jest wymagana");
      return;
    }
    if (Number.isNaN(qty) || qty <= 0) {
      setError(`Ilość musi być liczbą większą od 0 (${formData.quantity_type})`);
      return;
    }
    if (Number.isNaN(ppp) || ppp < 0) {
      setError("Cena jednostkowa musi być liczbą nieujemną");
      return;
    }

    const payload = {
      order_id: orderId,
      category: FIXED_CATEGORY, // ✅ zostaje, ale nie pokazujemy już w UI
      ...formData,
      name, // przycięte
      price_per_piece: round2(ppp),
      price: totalPrice, // ✅ łączna
    };

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/materials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let message = "Błąd dodawania materiału";
        try {
          const data = await response.json();
          if (data?.error) message = data.error;
        } catch {}
        throw new Error(message);
      }

      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Wystąpił nieznany błąd");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mm-overlay" onClick={onClose}>
      <div
        className="mm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="material-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mm-header">
          <div className="mm-header-text">
            <h3 id="material-modal-title">Dodaj Materiał</h3>
            <p className="mm-subtitle">
              Uzupełnij dane materiału oraz ilość i cenę.
            </p>
          </div>

          <button
            type="button"
            className="mm-close-btn"
            aria-label="Zamknij"
            title="Zamknij"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {/* Error */}
        {error && (
          <p className="mm-error" role="alert" aria-live="assertive">
            {error}
          </p>
        )}

        {/* Body */}
        <form onSubmit={handleSubmit} noValidate className="mm-body">
          {/* Sekcja 1 */}
          <div className="mm-card">
            <div className="mm-card-title">Podstawowe informacje</div>

            <div className="mm-row">
              <div className="mm-group">
                <label htmlFor="name">Nazwa: *</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder="..."
                />
              </div>

              <div className="mm-group">
                <label htmlFor="manufacturer">Producent:</label>
                <input
                  id="manufacturer"
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="..."
                />
              </div>
            </div>

            <div className="mm-row">
              <div className="mm-group">
                <label htmlFor="symbol">Symbol:</label>
                <input
                  id="symbol"
                  type="text"
                  name="symbol"
                  value={formData.symbol}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="..."
                />
              </div>

              <div className="mm-group">
                <label htmlFor="designation">Numer / Serial / Oznaczenie:</label>
                <input
                  id="designation"
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="..."
                />
              </div>
            </div>

            <div className="mm-group">
              <label htmlFor="additional_description">Dodatkowy opis:</label>
              <textarea
                id="additional_description"
                name="additional_description"
                value={formData.additional_description}
                onChange={handleInputChange}
                disabled={isSubmitting}
                placeholder="Opcjonalne informacje dodatkowe..."
              />
            </div>
          </div>

          {/* Sekcja 2 (ukryta w UI, ale zostaje w kodzie i payload) */}
          {SHOW_IDENTIFIERS_SECTION && (
            <div className="mm-card">
              <div className="mm-card-title">Numery / identyfikatory</div>

              <div className="mm-row mm-row-3">
                <div className="mm-group">
                  <label htmlFor="catalog_number">Numer katalogowy:</label>
                  <input
                    id="catalog_number"
                    type="text"
                    name="catalog_number"
                    value={formData.catalog_number}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mm-group">
                  <label htmlFor="serial_number">Numer seryjny:</label>
                  <input
                    id="serial_number"
                    type="text"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mm-group">
                  <label htmlFor="mac_address">Adres MAC:</label>
                  <input
                    id="mac_address"
                    type="text"
                    name="mac_address"
                    value={formData.mac_address}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder="00:11:22:33:44:55"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Sekcja 3 */}
          <div className="mm-card">
            <div className="mm-card-title">Ilość i cena</div>

            <div className="mm-row">
              <div className="mm-group">
                <label htmlFor="quantity_type">Typ ilości: *</label>
                <select
                  id="quantity_type"
                  name="quantity_type"
                  value={formData.quantity_type}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  <option value="metry">Metry</option>
                  <option value="sztuki">Sztuki</option>
                </select>
              </div>

              <div className="mm-group">
                <label htmlFor="quantity">
                  {formData.quantity_type === "metry"
                    ? "Ilość metrów: *"
                    : "Ilość sztuk: *"}
                </label>
                <input
                  id="quantity"
                  type="number"
                  step={formData.quantity_type === "metry" ? "0.1" : "1"}
                  min={formData.quantity_type === "metry" ? "0.1" : "1"}
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="mm-row">
              <div className="mm-group">
                <label htmlFor="price_per_piece">{unitPriceLabel}</label>
                <input
                  id="price_per_piece"
                  type="number"
                  step="0.01"
                  min="0"
                  name="price_per_piece"
                  value={formData.price_per_piece}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mm-group">
                <label htmlFor="currency">Waluta: *</label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  <option value="zł">zł</option>
                  <option value="$">$</option>
                  <option value="€">€</option>
                </select>
              </div>
            </div>

            {/* Podsumowanie */}
            <div className="mm-total">
              <div className="mm-total-label">Łączna cena</div>
              <div className="mm-total-value">
                {totalPrice || 0} {formData.currency}
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="mm-footer">
            <button
              type="button"
              className="mm-btn mm-btn-ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Anuluj
            </button>

            <button
              type="submit"
              className="mm-btn mm-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Zapisywanie…" : "Zapisz"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MaterialModal;
